import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import FlipIcon from '@mui/icons-material/Flip';
import Confetti from 'react-confetti';
import { hapticLevelComplete } from '../../utils/haptics';
import { playSantoorResults } from '../../utils/audioEngine';


const GameResults = ({ score, stars, levelId, levelName, noteScores, onPlayAgain, onNextLevel, onHome, isLastLevel }) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [animateStars, setAnimateStars] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    playSantoorResults();
    hapticLevelComplete(stars);
    if (stars === 5) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    }
    setTimeout(() => setAnimateStars(true), 500);
  }, [stars]);

  const handleFlip = () => {
    setIsFlipped(f => !f);
  };

  const getScoreMessage = () => {
    if (stars === 5) return "🎉 PERFECT! Absolutely Amazing!";
    if (stars === 4) return "⭐ Excellent Work!";
    if (stars >= 3) return "✨ Great Job! Next Level Unlocked!";
    if (stars === 2) return "👍 Good Effort! Try Again for 3 Stars";
    return "💪 Keep Practicing!";
  };

  const getScoreColor = () => {
    if (score >= 90) return '#4caf50';
    if (score >= 70) return '#ff9800';
    return '#f44336';
  };

  const noteColor = (s) => s >= 75 ? '#2e7d32' : s >= 55 ? '#f57c00' : '#c62828';
  const noteBg   = (s) => s >= 75 ? '#e8f5e9' : s >= 55 ? '#fff3e0' : '#ffebee';

  // ── Arc star row — translateY-only (no position:absolute) so iOS backface doesn't bleed ──
  // Stars curve upward toward the center; accuracy % floats in the dip beneath center star.
  // Perfect semicircle: r=64, positions at -72°,-36°,0°,36°,72° from top
  const ARC_R = 64;
  const ARC_ANGLES = [-72, -36, 0, 36, 72].map(deg => (deg * Math.PI) / 180);
  const ARC_Y = ARC_ANGLES.map(a => ARC_R - ARC_R * Math.cos(a)); // 0 at top, rises toward edges
  const renderArcStars = () => (
    <Box sx={{ position: 'relative', width: '100%', mt: 2, mb: 0 }}>
      {/* The star arc row */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: { xs: 1.6, sm: 2.2 } }}>
        {[1, 2, 3, 4, 5].map((star, i) => {
          const filled = star <= stars;
          return (
            <Box key={star} sx={{ transform: `translateY(${ARC_Y[i]}px)`, lineHeight: 0 }}>
              {filled
                ? <StarIcon sx={{
                    fontSize: { xs: 56, sm: 62 },
                    color: '#ffd700',
                    filter: 'drop-shadow(0 3px 8px rgba(255,215,0,0.7))',
                    animation: animateStars ? `starPop${star} 0.45s ease-out ${star * 0.12}s both` : 'none',
                    [`@keyframes starPop${star}`]: {
                      '0%':   { transform: 'scale(0)', opacity: 0 },
                      '60%':  { transform: 'scale(1.45)' },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }} />
                : <StarBorderIcon sx={{ fontSize: { xs: 56, sm: 62 }, color: '#d4d4d4' }} />}
            </Box>
          );
        })}
      </Box>
      {/* Accuracy badge — sits below the arc, shifted down for breathing room */}
      <Box sx={{
        mt: `${Math.round(ARC_Y[4]) + 28}px`,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <Box sx={{
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
          px: 2.5, py: 1, borderRadius: 4,
          background: `${getScoreColor()}18`,
          border: `2px solid ${getScoreColor()}44`,
        }}>
          <Typography sx={{
            fontSize: '2.8rem', fontWeight: 900, lineHeight: 1,
            color: getScoreColor(),
            animation: animateStars ? 'fadeInUp 0.7s ease-out 0.7s both' : 'none',
            '@keyframes fadeInUp': {
              '0%':   { opacity: 0, transform: 'translateY(10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}>
            {score}%
          </Typography>
          <Typography variant="caption" sx={{
            letterSpacing: 1.2, textTransform: 'uppercase',
            fontSize: '0.62rem', color: getScoreColor(), fontWeight: 700, opacity: 0.8,
          }}>
            Accuracy
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // ── Card faces ──────────────────────────────────────────────────────────────
  const hasNoteScores = noteScores && noteScores.length > 0;
  const noteCount = noteScores?.length || 0;
  // Fixed card height — content scrolls inside rather than card growing.
  const CARD_HEIGHT = 460;

  const frontFace = (
    <Paper
      elevation={4}
      sx={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        p: 3, textAlign: 'center',
        background: 'linear-gradient(160deg, #f9fafb 0%, #e8f5e0 100%)',
        borderRadius: 3,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Level {levelId} Complete!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {levelName}
      </Typography>

      {/* Arc stars with accuracy badge in the valley */}
      {renderArcStars()}

      <Typography variant="body1" sx={{ fontWeight: 700, color: '#444', textAlign: 'center', px: 2, mt: 'auto', mb: 1.5 }}>
        {getScoreMessage()}
      </Typography>

      {/* Tap-for-stats hint — pointer-events:none so card onClick handles the tap */}
      {hasNoteScores && (
        <Box sx={{
          mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          px: 3, py: 1.2, borderRadius: 6,
          background: 'rgba(88,204,2,0.15)',
          border: '1.5px solid rgba(88,204,2,0.35)',
          pointerEvents: 'none',
          animation: 'shimmerHint 2.4s ease-in-out infinite',
          '@keyframes shimmerHint': {
            '0%,100%': { opacity: 0.75, transform: 'scale(1)' },
            '50%':     { opacity: 1,    transform: 'scale(1.04)' },
          },
        }}>
          <FlipIcon sx={{ fontSize: 20, color: '#58CC02' }} />
          <Typography variant="body2" sx={{ color: '#58CC02', fontWeight: 700, fontSize: '0.95rem' }}>
            Tap for stats
          </Typography>
        </Box>
      )}
    </Paper>
  );

  const backFace = (
    <Paper
      elevation={4}
      sx={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        // translateZ(2px) pushes back face forward in z-space — standard WebKit fix
        // to prevent front-face elements from bleeding through on iOS Safari.
        transform: 'rotateY(180deg) translateZ(2px)',
        p: 3,
        background: 'linear-gradient(160deg, #f0f4ff 0%, #e8f5e0 100%)',
        borderRadius: 3,
        display: 'flex', flexDirection: 'column',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* Back face header: title + flip hint on one line, stars as underline below */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlipIcon sx={{ fontSize: 18, color: '#58CC02' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
            Performance Breakdown
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            tap to flip back
          </Typography>
        </Box>
        {/* Stars as underline — slightly bigger, straight row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, pl: '26px', mt: 0.3 }}>
          {[1, 2, 3, 4, 5].map((s) =>
            s <= stars
              ? <StarIcon key={s} sx={{ fontSize: 17, color: '#ffd700', filter: 'drop-shadow(0 1px 3px rgba(255,215,0,0.5))' }} />
              : <StarBorderIcon key={s} sx={{ fontSize: 17, color: '#ccc' }} />
          )}
        </Box>
      </Box>

      {/* Summary row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(88,204,2,0.1)' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: getScoreColor() }}>{score}%</Typography>
          <Typography variant="caption" color="text.secondary">Overall</Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,215,0,0.15)' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#f9a825' }}>{stars}/5</Typography>
          <Typography variant="caption" color="text.secondary">Stars</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Per-note rows — always scrollable, fills remaining card height */}
      {hasNoteScores ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
          {noteScores.map((ns, i) => (
            <Box key={i} sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              p: 1.2, borderRadius: 1.5, backgroundColor: noteBg(ns.score),
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{ns.noteDisplay}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, color: noteColor(ns.score) }}>
                  {Math.round(ns.score)}%
                </Typography>
                {ns.score >= 85 && <StarIcon sx={{ fontSize: 14, color: '#ffd700' }} />}
              </Box>
            </Box>
          ))}
          <Typography variant="caption" sx={{ mt: 0.5, color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
            Each note scored on best 3-second window
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          No per-note data for this level.
        </Typography>
      )}
    </Paper>
  );

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={500} />
      )}

      {/* ── Flip Card — fixed height, content scrolls inside —── */}
      <Box
        onClick={hasNoteScores ? handleFlip : undefined}
        sx={{
          perspective: '1200px',
          cursor: hasNoteScores ? 'pointer' : 'default',
          mb: 3,
          height: CARD_HEIGHT,
          touchAction: 'manipulation',
        }}
        role={hasNoteScores ? 'button' : undefined}
        aria-label={hasNoteScores ? (isFlipped ? 'Flip back to stars' : 'Tap to see note stats') : undefined}
      >
        <Box sx={{
          position: 'relative',
          height: CARD_HEIGHT,
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          // Slow down through the 90° midpoint so the inverted-text frame lingers.
          transition: 'transform 0.85s cubic-bezier(0.25, 0.8, 0.25, 1)',
        }}>
          {frontFace}
          {backFace}
        </Box>
      </Box>

      {/* ── Action Buttons ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

        {/* Primary CTA: next level — biggest, boldest, animated pulse */}
        {stars >= 3 && !isLastLevel && (
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={onNextLevel}
            sx={{
              '&.MuiButton-root': {
                background: 'linear-gradient(90deg, #58CC02 0%, #3da801 100%) !important',
                color: '#fff',
                fontWeight: 900,
                fontSize: '1.1rem',
                letterSpacing: 0.8,
                py: 2,
                borderRadius: 3,
                boxShadow: '0 6px 22px rgba(88,204,2,0.5)',
                textTransform: 'none',
                animation: 'ctaPulse 2s ease-in-out infinite',
              },
              '@keyframes ctaPulse': {
                '0%,100%': { boxShadow: '0 6px 22px rgba(88,204,2,0.5)' },
                '50%':     { boxShadow: '0 6px 32px rgba(88,204,2,0.85)' },
              },
              '&.MuiButton-root:hover': { background: 'linear-gradient(90deg, #46a302 0%, #3a8c01 100%) !important' },
            }}
          >
            Continue to Next Level
          </Button>
        )}

        {/* Secondary CTA: play again */}
        <Button
          variant="contained"
          size="large"
          startIcon={<ReplayIcon />}
          onClick={onPlayAgain}
          sx={{
            '&.MuiButton-root': {
              background: stars >= 3 && !isLastLevel
                ? 'rgba(88,204,2,0.12) !important'
                : 'linear-gradient(90deg, #58CC02 0%, #3da801 100%) !important',
              color: stars >= 3 && !isLastLevel ? '#2e7d00' : '#fff',
              fontWeight: 700,
              fontSize: '1rem',
              py: 1.8,
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: stars >= 3 && !isLastLevel ? 'none' : '0 6px 22px rgba(88,204,2,0.5)',
              border: stars >= 3 && !isLastLevel ? '2px solid rgba(88,204,2,0.4)' : 'none',
              animation: (stars < 3 || isLastLevel) ? 'ctaPulse 2s ease-in-out infinite' : 'none',
            },
            '@keyframes ctaPulse': {
              '0%,100%': { boxShadow: '0 6px 22px rgba(88,204,2,0.5)' },
              '50%':     { boxShadow: '0 6px 32px rgba(88,204,2,0.85)' },
            },
            '&.MuiButton-root:hover': {
              background: stars >= 3 && !isLastLevel
                ? 'rgba(88,204,2,0.2) !important'
                : 'linear-gradient(90deg, #46a302 0%, #3a8c01 100%) !important',
            },
          }}
        >
          Play Again
        </Button>

        {/* Tertiary: go home */}
        <Button
          variant="outlined"
          size="medium"
          startIcon={<HomeIcon />}
          onClick={onHome}
          sx={{
            '&.MuiButton-root': {
              color: '#777',
              borderColor: 'rgba(0,0,0,0.15)',
              fontWeight: 500,
              fontSize: '0.9rem',
              py: 1,
              borderRadius: 3,
              textTransform: 'none',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>

      {/* ── Status banners ── */}
      {stars >= 3 && !isLastLevel && (
        <Box sx={{ mt: 2.5, p: 1.5, backgroundColor: '#d4edda', borderRadius: 2, border: '1px solid #c3e6cb', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#155724', fontWeight: 700 }}>
            🎊 Next Level Unlocked!
          </Typography>
        </Box>
      )}
      {stars < 3 && (
        <Box sx={{ mt: 2.5, p: 1.5, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            💡 Score 3 stars or more to unlock the next level
          </Typography>
        </Box>
      )}
      {isLastLevel && (
        <Box sx={{ mt: 2.5, p: 1.5, backgroundColor: '#ffd700', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#000', fontWeight: 700 }}>
            🏆 You've completed all available levels!
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default GameResults;

